import { Button, FlexCenter, GlassFrame } from "@9amhealth/shared";
import styled from "@emotion/styled";
import type { FC } from "react";
import { useEffect, useCallback, useRef } from "react";
import { BiometricVerificationBloc } from "src/hybrid/components/BiometricVerification";
import { useBloc } from "src/state/state";
import logo9am from "src/ui/assets/icons/Logo9amWhiteShort.svg";
import FaceIdIcon from "src/ui/components/GlobalOverlays/assets/FaceIdIcon";
import LockIcon from "src/ui/components/GlobalOverlays/assets/LockIcon";
import TouchIdIcon from "src/ui/components/GlobalOverlays/assets/TouchIdIcon";
import SunriseBackground from "src/ui/components/SunriseBackground/SunriseBackground";
import Translate from "src/ui/components/Translate/Translate";
import type { TranslationKey } from "src/types/translationKey";
import { BiometryType } from "@capgo/capacitor-native-biometric";

const LockedContent = styled.div`
  label: LockedContent;
  position: relative;
  z-index: 3;
  padding: 1.6rem;
  padding-top: calc(var(--safe-area-inset-top, 0) + 2em);
  padding-bottom: calc(var(--safe-area-inset-bottom, 0) + 2em);
  margin: 0 auto;
  width: min(100%, 400px);
  height: 100%;

  .logo-frame {
    aspect-ratio: 1;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const Text = styled.p`
  text-align: center;
  color: #000;
  text-align: center;
  font-feature-settings:
    "liga" off,
    "clig" off;

  /* Body/Little1 */
  font-family: "STK Bureau";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%; /* 19.6px */
  letter-spacing: -0.28px;
`;

const getBiometricButtonText = (
  biometricType: BiometryType
): TranslationKey => {
  switch (biometricType) {
    case BiometryType.FACE_ID:
      return "appLocked.signInWith.faceId";
    case BiometryType.TOUCH_ID:
      return "appLocked.signInWith.touchId";
    case BiometryType.FINGERPRINT:
      return "appLocked.signInWith.fingerprint";
    default:
      return "appLocked.signInWith.default";
  }
};

const LockedScreen: FC = () => {
  const [
    { biometricType, isBiometricVerificationAvailable },
    { verifyIdentity, requestEmailLogin }
  ] = useBloc(BiometricVerificationBloc);
  const hasTriggeredRef = useRef(false);
  const biometricButtonText = getBiometricButtonText(biometricType);

  const getIcon = useCallback(() => {
    switch (biometricType) {
      case 2: // FACE_ID
        return <FaceIdIcon />;
      case 1: // TOUCH_ID
      case 3: // FINGERPRINT
        return <TouchIdIcon />;
      default:
        return <LockIcon />;
    }
  }, [biometricType]);

  const handleUnlock = useCallback(async () => {
    try {
      await verifyIdentity(true);
    } catch {
      // Verification failed - user can try again
    }
  }, [verifyIdentity]);

  // Auto-trigger biometric verification on mount (once) if biometrics available
  useEffect(() => {
    if (hasTriggeredRef.current || !isBiometricVerificationAvailable) return;
    hasTriggeredRef.current = true;

    const timer = setTimeout(() => {
      void handleUnlock();
    }, 30);
    return () => clearTimeout(timer);
  }, [handleUnlock, isBiometricVerificationAvailable]);

  return (
    <SunriseBackground>
      <LockedContent>
        <FlexCenter y="bottom" fullHeight alignItems="center">
          <GlassFrame className="logo-frame">
            <img src={logo9am} alt="9am Health" width={99} height={37} />
          </GlassFrame>

          {isBiometricVerificationAvailable ? (
            <>
              <Button
                theme="glass"
                center
                onPress={() => void handleUnlock()}
                hideArrow
                grow
                icon={getIcon()}
                isActionButton={false}
              >
                <Translate msg={biometricButtonText} />
              </Button>
              <div style={{ height: "var(--space-sm)" }} />
            </>
          ) : null}

          <Button theme="glass" center onPress={requestEmailLogin} grow>
            <Translate msg="appLocked.continueWithEmail" />
          </Button>
          <div style={{ height: "var(--space-sm)" }} />

          <Text>
            <Translate msg="appLocked.support.text" />
            <br />
            <Translate msg="appLocked.support.phone" />
          </Text>
        </FlexCenter>
      </LockedContent>
    </SunriseBackground>
  );
};

export default LockedScreen;
